import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  Pagination,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useAssessmentReport } from './useAssessmentReport';
import { formatCurrency, formatDate, EMPTY } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_relat_rio_de_apura_o_anal_tica`
   → Relatório de Apuração Analítica — GET /fiscal/assessment-report.

   Da referência vieram: os indicadores do período (documentos, base, imposto,
   alíquota média), o filtro por competência e a tabela densa com
   Tipo, NF/Série, Data Emissão, CFOP, Base de Cálculo, Alíquota % e
   o detalhamento por tributo (Imp. Debitado/Creditado).

   Não vieram (não existem no backend):
   * aba "Entradas com direito a crédito" — não há distinção de crédito no
     contrato;
   * coluna "LFE Reports" / auditoria externa.

   O contrato agrega por tributo em `summary.byTaxType[]` e por linha em
   `byTaxType` (mapa chaveado por tributo). A tabela renderiza o mapa da linha
   como pills de tributo; o resumo vira uma segunda tabela.
   ─────────────────────────────────────────────────────────────────────────── */

const TYPE_OPTIONS = [
  { value: 'ALL', label: 'Todos os tipos' },
  { value: 'NFE', label: 'NF-e' },
  { value: 'NFCE', label: 'NFC-e' },
  { value: 'NFSE', label: 'NFS-e' },
];

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  PENDING: 'Processando',
  AUTHORIZED: 'Autorizada',
  CANCELLED: 'Cancelada',
  REJECTED: 'Rejeitada',
  DENIED: 'Denegada',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral' | 'info'> = {
  DRAFT: 'neutral',
  PENDING: 'info',
  AUTHORIZED: 'success',
  CANCELLED: 'neutral',
  REJECTED: 'error',
  DENIED: 'error',
};

function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return EMPTY;
  return `${Number(v).toFixed(1).replace('.', ',')}%`;
}

const PAGE_SIZE = 25;

export function AssessmentReportPage(): JSX.Element {
  const hoje = new Date();
  const competenciaAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;

  const [typeFilter, setTypeFilter] = useState('ALL');
  const [cfopFilter, setCfopFilter] = useState('');
  const [competencia, setCompetencia] = useState(competenciaAtual);
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useAssessmentReport({
    type: typeFilter,
    cfop: cfopFilter || undefined,
    competencia,
    page,
    limit: PAGE_SIZE,
  });

  const lines = data?.data ?? [];
  const summary = data?.summary;
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  return (
    <div className="assessment-report-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Relatório de Apuração Analítica
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Detalhamento dos documentos fiscais do período, com base, alíquota e imposto por tributo.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div className="assessment-filters">
          <Select label="Tipo de documento" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Input
            label="CFOP (prefixo ou exato)"
            placeholder="Ex.: 5102"
            value={cfopFilter}
            onChange={(e) => { setCfopFilter(e.target.value.replace(/\D/g, '')); setPage(1); }}
          />
          <Input
            label="Competência"
            type="month"
            value={competencia}
            onChange={(e) => { setCompetencia(e.target.value); setPage(1); }}
          />
        </div>
      </Card>

      {/* Summary */}
      {isLoading ? (
        <LoadingSkeleton count={4} height="96px" borderRadius="12px" />
      ) : error ? (
        <Card className="assessment-report__error">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao carregar o relatório</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f' }}>
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <Button variant="primary" onClick={() => refetch()}>Tentar novamente</Button>
        </Card>
      ) : (
        <div className="assessment-summary">
          <div className="assessment-summary__item">
            <span className="assessment-summary__label">Documentos</span>
            <strong className="assessment-summary__value">{summary?.totalDocuments ?? 0}</strong>
          </div>
          <div className="assessment-summary__item">
            <span className="assessment-summary__label">Base de cálculo</span>
            <strong className="assessment-summary__value">{formatCurrency(summary?.totalBaseCalculo)}</strong>
          </div>
          <div className="assessment-summary__item">
            <span className="assessment-summary__label">Imposto apurado</span>
            <strong className="assessment-summary__value">{formatCurrency(summary?.totalImposto)}</strong>
          </div>
          <div className="assessment-summary__item">
            <span className="assessment-summary__label">Alíquota média</span>
            <strong className="assessment-summary__value">{formatPct(summary?.aliquotaMediaPct)}</strong>
          </div>
        </div>
      )}

      {/* Per-tax summary */}
      {!isLoading && !error && (summary?.byTaxType.length ?? 0) > 0 && (
        <Card variant="outlined" padding="0" className="assessment-taxes">
          <CardHeader className="px-md pt-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Agregado por tributo</h3>
          </CardHeader>
          <div style={{ overflowX: 'auto' }}>
            <table className="fiscal-table">
              <thead>
                <tr>
                  <th>Tributo</th>
                  <th align="right">Base (R$)</th>
                  <th align="right">Imposto (R$)</th>
                  <th align="right">Alíquota média</th>
                </tr>
              </thead>
              <tbody>
                {summary?.byTaxType.map((t) => (
                  <tr key={t.taxType}>
                    <td><strong>{t.label}</strong><small className="cell-sub">{t.taxType}</small></td>
                    <td align="right" className="tabular-nums">{formatCurrency(t.base)}</td>
                    <td align="right" className="tabular-nums">{formatCurrency(t.value)}</td>
                    <td align="right" className="tabular-nums">{formatPct(t.aliquotaMediaPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail table */}
      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md" action={
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
            {data?.total ?? 0} documentos
          </span>
        }>
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Detalhamento de Documentos Fiscais</h3>
        </CardHeader>
        {lines.length === 0 && !isLoading && !error && (
          <div className="assessment-report__empty">
            <p className="m-0">Nenhum documento encontrado para os filtros informados.</p>
          </div>
        )}
        {lines.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="fiscal-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>NF / Série</th>
                  <th>Data Emissão</th>
                  <th>CFOP</th>
                  <th align="right">Base (R$)</th>
                  <th align="right">Alíq. média</th>
                  <th align="right">Imposto (R$)</th>
                  <th>Tributos</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const taxTypes = Object.entries(l.byTaxType).slice(0, 4);
                  return (
                    <tr key={l.id}>
                      <td><strong>{l.tipo}</strong></td>
                      <td className="tabular-nums">
                        {l.numero ? `${l.numero}${l.serie ? ` / ${l.serie}` : ''}` : '—'}
                      </td>
                      <td className="tabular-nums">{formatDate(l.dataEmissao)}</td>
                      <td className="tabular-nums">{l.cfop}</td>
                      <td align="right" className="tabular-nums">{formatCurrency(l.baseCalculo)}</td>
                      <td align="right" className="tabular-nums">{formatPct(l.aliquotaMediaPct)}</td>
                      <td align="right" className="tabular-nums">{formatCurrency(l.valorImposto)}</td>
                      <td>
                        <div className="tax-chips" title={Object.keys(l.byTaxType).join(', ')}>
                          {taxTypes.map(([t, _]) => <span key={t} className="tax-chip">{t}</span>)}
                          {Object.keys(l.byTaxType).length > 4 && (
                            <span className="tax-chip tax-chip--more">+{Object.keys(l.byTaxType).length - 4}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge size="sm" variant={STATUS_VARIANT[l.status] ?? 'neutral'}>
                          {STATUS_LABEL[l.status] ?? l.status}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="assessment-report__pagination">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>

      <style>{`
        .assessment-report-page { padding: 1.5rem; }
        .assessment-filters { display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap; }
        .assessment-filters > * { flex: 1; min-width: 180px; }
        .assessment-report__error { padding: 2rem; text-align: center; display: grid; gap: 0.5rem; justify-items: center; color: #74777f; }
        .assessment-report__empty { padding: 2rem; text-align: center; color: #74777f; font-size: 0.875rem; }
        .assessment-report__pagination { display: flex; justify-content: flex-end; padding: 0.75rem 1rem; }
        .assessment-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
        .assessment-summary__item { background: #fff; border: 1px solid #e0e3e5; border-radius: 10px; padding: 1rem; display: flex; flex-direction: column; gap: 0.25rem; }
        .assessment-summary__label { color: #74777f; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; }
        .assessment-summary__value { font-size: 1.25rem; font-weight: 700; color: #002045; font-variant-numeric: tabular-nums; }
        .assessment-taxes { margin-bottom: 1rem; }
        .fiscal-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .fiscal-table th {
          text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .fiscal-table td { padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .fiscal-table tbody tr:hover { background: #f8fafc; }
        .cell-sub { display: block; color: #74777f; font-size: 0.75rem; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
        .tax-chips { display: inline-flex; gap: 0.25rem; flex-wrap: wrap; max-width: 15ch; }
        .tax-chip { background: #eef2f7; color: #002045; border-radius: 999px; padding: 0.125rem 0.5rem; font-size: 0.6875rem; font-weight: 600; }
        .tax-chip--more { background: transparent; color: #74777f; }
      `}</style>
    </div>
  );
}